import { c as _c } from "react/compiler-runtime";
import { useFormatter, useTranslations } from 'next-intl';
import { D9_SCALE } from '@/config/solana';
import { useFormattedCurrency } from '@/lib/currency';
import { formatCount } from '@/lib/format/format-count';
import type { ComparisonUnit } from './resolution-integrity-types';
interface MetricFormat {
  d6: (value: string | null) => string;
  count: (value: number | null) => string;
  /** `value` is ppb (1e9 = 100%); signed shows an explicit +/− for net flows. */
  percent: (value: number | string | null, signed?: boolean) => string;
  byUnit: (value: string | null, unit: ComparisonUnit, signed?: boolean) => string;
}
export function useMetricFormat() {
  const $ = _c(21);
  const {
    formatD6,
    locale
  } = useFormattedCurrency();
  const intl = useFormatter();
  const t0 = useTranslations("market.resolutionIntegrity");
  let t1;
  if ($[0] !== t0) {
    t1 = t0("unavailable");
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const missing = t1;
  let t2;
  if ($[2] !== formatD6 || $[3] !== missing) {
    t2 = value => value === null ? missing : formatD6(value, "compact");
    $[2] = formatD6;
    $[3] = missing;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const d6 = t2;
  let t3;
  if ($[5] !== locale || $[6] !== missing) {
    t3 = value_0 => value_0 === null ? missing : formatCount(value_0, locale);
    $[5] = locale;
    $[6] = missing;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const count = t3;
  let t4;
  if ($[8] !== intl || $[9] !== missing) {
    t4 = (value_1, t5) => {
      const signed = t5 === undefined ? false : t5;
      return value_1 === null ? missing : intl.number(Number(value_1) / D9_SCALE, {
        style: "percent",
        maximumFractionDigits: 2,
        roundingMode: "trunc",
        signDisplay: signed ? "exceptZero" : "auto"
      });
    };
    $[8] = intl;
    $[9] = missing;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const percent = t4;
  let t5;
  if ($[11] !== count || $[12] !== d6 || $[13] !== missing || $[14] !== percent) {
    t5 = (value_2, unit, t6) => {
      const signed_0 = t6 === undefined ? false : t6;
      if (value_2 === null) {
        return missing;
      }
      switch (unit) {
        case "usdD6":
          {
            return d6(value_2);
          }
        case "count":
          {
            return count(Number(value_2));
          }
        case "ppb":
          {
            return percent(value_2, signed_0);
          }
      }
    };
    $[11] = count;
    $[12] = d6;
    $[13] = missing;
    $[14] = percent;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const byUnit = t5;
  let t6;
  if ($[16] !== byUnit || $[17] !== count || $[18] !== d6 || $[19] !== percent) {
    t6 = {
      d6,
      count,
      percent,
      byUnit
    };
    $[16] = byUnit;
    $[17] = count;
    $[18] = d6;
    $[19] = percent;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}